/** The account's streak on Supabase — one row per reader in `tdg_streaks`.
 *
 *  The remote half of `services/streakSync.ts`, and the only file that knows
 *  the streak has a table at all. It is a plain own-row table rather than the
 *  `SECURITY DEFINER` RPCs the rest of `remoteAccount.ts` needs: those exist
 *  because `profiles` is shared with every other TDG app, and nobody but the
 *  reader ever reads their own streak. RLS is own-row-only in both directions,
 *  so the row can be selected and upserted directly.
 *
 *  ── Failing quietly is the contract ──────────────────────────────────────
 *  A streak must keep counting with no network, on a device-local account, and
 *  on a build whose `tdg-core` has not had the migration applied yet. So every
 *  call here answers "not available" instead of throwing at the UI: `fetch`
 *  returns null and `save` reports false. The device record is untouched either
 *  way and the reader sees their streak exactly as before. A schema that is not
 *  there yet latches `unavailable` for the session, so a build running against
 *  an older `tdg-core` does not retry a 404 on every sign-in and every day
 *  rollover.
 *
 *  ── The table moved, and gained a column ─────────────────────────────────
 *  It was `bea_streaks`, keyed on the account alone, until 2026-08-28. It is
 *  now `public.tdg_streaks`, keyed `(user_id, app)`, for the reason
 *  `tdg_badges` is: a streak is a run of days an ACCOUNT kept, and a second
 *  TDG app wanting one must not need a migration. Every row this app reads or
 *  writes carries `app: 'bea'`, and the upsert conflicts on BOTH columns —
 *  `onConflict: 'user_id'` against a composite key does not merely pick the
 *  wrong row, Postgres refuses the statement outright.
 *
 *  A build older than that migration keeps counting locally and latches
 *  `unavailable` for the session, which is exactly what the paragraph above
 *  describes and is why this rename costs a deployed reader nothing.
 *
 *  Schema: `supabase/migrations/20260818200000_bea_streak_sync.sql`, and
 *  TDG-Site's `20260828090000_tdg_privacy_and_table_merges.sql` for the move. */
import { supabase, supabaseConfigured } from './supabaseClient';
import { activeAuth } from './authAdapter';
import { BLANK_STREAK, type DayRun, type StreakState } from '../streak';

/** This app's id in every shared TDG table — `tdg_streaks`, `tdg_badges`,
 *  `tdg_feedback`. One constant, because a typo in one of them is a row nobody
 *  ever reads back rather than an error anybody sees. */
const BEA_APP = 'bea';

/** How a streak record crosses the wire. `date` columns come back from
 *  PostgREST as `YYYY-MM-DD` — the app's own day-key format — and a day nobody
 *  recorded is SQL NULL rather than the empty string the client uses. */
interface StreakRow {
  user_id: string;
  /** Always `'bea'` from this app. The column is what lets one account keep a
   *  separate run in each TDG app rather than one shared figure. */
  app: string;
  current: number;
  longest: number;
  last_active_date: string | null;
  total_days: number;
  current_start: string | null;
  best_start: string | null;
  best_end: string | null;
  best_is_current: boolean;
  dates_from: string | null;
  /** The run ledger, as `[{start, end}]`. The account row has to carry it or
   *  every device would rebuild its own from the two summary runs, and a third
   *  run's days would drift into `undated` and be counted again. */
  runs: DayRun[];
  undated: number;
}

/** Server JSON is untrusted input like any other: take only well-formed day
 *  runs, so one bad row cannot corrupt a reader's history on every device. */
function toRuns(v: unknown): DayRun[] {
  if (!Array.isArray(v)) return [];
  return v
    .filter((r): r is DayRun => !!r && typeof (r as DayRun).start === 'string'
      && typeof (r as DayRun).end === 'string'
      && (r as DayRun).start <= (r as DayRun).end)
    .map((r) => ({ start: r.start, end: r.end }));
}

/** The seam `streakSync` talks to. An interface, not a direct import, so the
 *  sync rules can be driven against a fake cloud in tests, and so a future
 *  non-Supabase backend is one implementation rather than a rewrite. */
export interface StreakRemote {
  /** Is there a backend to talk to at all right now? */
  available(): boolean;
  /** This account's stored record, or null when there is none / it could not
   *  be read. Never throws. */
  fetch(userId: string): Promise<StreakState | null>;
  /** Store the merged record. Returns false when it did not land. Never throws. */
  save(userId: string, s: StreakState): Promise<boolean>;
}

/** Postgres / PostgREST codes that mean "this build is talking to a `tdg-core`
 *  without the streak table" rather than "something went wrong once".
 *  42P01 = undefined table, 42501 = no GRANT, PGRST20x = not in the schema
 *  cache. All are permanent for the session; retrying only burns requests. */
const SCHEMA_MISSING = new Set(['42P01', '42501', 'PGRST106', 'PGRST202', 'PGRST205']);

const day = (v: string | null): string => v ?? '';
const orNull = (v: string): string | null => v || null;

function rowToState(r: StreakRow): StreakState {
  return {
    current: r.current ?? 0,
    longest: r.longest ?? 0,
    lastActiveDate: day(r.last_active_date),
    totalDays: r.total_days ?? 0,
    currentStart: day(r.current_start),
    bestStart: day(r.best_start),
    bestEnd: day(r.best_end),
    bestIsCurrent: !!r.best_is_current,
    datesFrom: day(r.dates_from),
    runs: toRuns(r.runs),
    undated: Math.max(0, r.undated ?? 0),
  };
}

function stateToRow(userId: string, s: StreakState): StreakRow {
  return {
    user_id: userId,
    app: BEA_APP,
    current: s.current,
    longest: s.longest,
    last_active_date: orNull(s.lastActiveDate),
    total_days: s.totalDays,
    current_start: orNull(s.currentStart),
    best_start: orNull(s.bestStart),
    best_end: orNull(s.bestEnd),
    best_is_current: s.bestIsCurrent,
    dates_from: orNull(s.datesFrom),
    runs: s.runs,
    undated: s.undated,
  };
}

class SupabaseStreakRemote implements StreakRemote {
  /** Latched once the server says the table is not there. */
  private schemaMissing = false;

  /** Whether it is worth ASKING. Deliberately does not touch the client: it is
   *  fetched on demand now (see supabaseClient.ts), and a synchronous "is it
   *  here yet?" would answer false on the very boot where the streak most
   *  wants to sync. `fetch`/`save` await it and fall back to null/false. */
  available(): boolean {
    return !this.schemaMissing && supabaseConfigured() && activeAuth.kind === 'remote';
  }

  /** Note a permanent schema problem so the rest of the session stops asking.
   *  Anything else (offline, a timeout, a 500) stays retryable — those come
   *  back on their own. */
  private note(error: { code?: string } | null): void {
    if (error?.code && SCHEMA_MISSING.has(error.code)) this.schemaMissing = true;
  }

  async fetch(userId: string): Promise<StreakState | null> {
    if (!this.available()) return null;
    try {
      const client = await supabase();
      if (!client) return null;
      const { data, error } = await client
        .from('tdg_streaks')
        .select('user_id, app, current, longest, last_active_date, total_days, current_start, best_start, best_end, best_is_current, dates_from, runs, undated')
        .eq('user_id', userId)
        .eq('app', BEA_APP)
        .maybeSingle();
      if (error) { this.note(error); return null; }
      // No row yet is not an error: this account has simply never synced. A
      // blank record merges as a no-op, which is exactly right.
      return data ? rowToState(data as unknown as StreakRow) : { ...BLANK_STREAK };
    } catch { return null; }
  }

  async save(userId: string, s: StreakState): Promise<boolean> {
    if (!this.available()) return false;
    try {
      const client = await supabase();
      if (!client) return false;
      const { error } = await client
        .from('tdg_streaks')
        .upsert({ ...stateToRow(userId, s), updated_at: new Date().toISOString() }, { onConflict: 'user_id,app' });
      if (error) { this.note(error); return false; }
      return true;
    } catch { return false; }
  }
}

/** The live remote. Inert — `available()` is false — on a device-local account,
 *  on a build with no Supabase project, and on a `tdg-core` without the table. */
export const supabaseStreakRemote: StreakRemote = new SupabaseStreakRemote();
